import { IdGen } from "@base/util/IdGen";
import { mapCount } from "@base/util/TypeUtil";
import { CompanyHelper } from "@biz/CompanyHelper";
import { OrderType } from "@biz/Definitions";
import { IArticle } from "@biz/definitions/ArticleDefinitions";
import { IBeosCompany } from "@biz/definitions/DatabaseDefinitions";
import { Product } from "@biz/definitions/Product";
import { FeatureLevel } from "@biz/definitions/ProductDefinitions";
import { ProductVersion } from "@biz/definitions/ProductVersion";
import { CornerType, PoleIndex, PoleType, SunSailType, TwistingSetType } from "@biz/definitions/SunSailDefinitions";
import { CurtainData } from "@biz/items/curtain/CurtainData";
import { Curtain2d, CurtainType, ICurtain2dForm } from "@biz/items/curtain/CurtainDefinitions";
import { RaffType } from "@biz/items/raff/RaffDefinitions";
import { SandyLogic } from "@biz/items/sunSail/logic/SandyLogic";
import { SunSailLogic } from "@biz/items/sunSail/logic/SunSailLogic";
import { createInitialSCPole, fillInitialPole } from "@biz/items/sunSail/SunSailInitial";
import { IProduct2dForm, ISail2dAMCXBaseForm, ISail2dBaseForm, ISail2dCSXSTwinBaseForm, ISail2dFixedForm, ISail2dForm, ISail2dProjectForm, ISail2dSandy, IStandardCollection2dForm, StandardCollection2dForm } from "@biz/models/Sail2dDefinitions";
import { ShoppingList } from "@biz/models/ShoppingList";
import { PD } from "@biz/PD";
import { IProductDescriptor, ISunSailDescriptor } from "@biz/ProductDescriptor";
import { ISCPole } from "@biz/sc/sunSail/ISCSunSailData";
import { IColorItem } from "@biz/SunSailColors";
import { ProjectOrderInfo } from "@biz/userProjects/new/IProject";
import { Customer, ProjectDealer } from "@biz/userProjects/new/IProjectDefinitions";

export const Sail2dForm = {
  createDefault(version: ProductVersion, featureLevel: FeatureLevel): ISail2dForm {
    return Sail2dForm.createSolidayC4(version, featureLevel)
  },
  createBase(version: ProductVersion, featureLevel: FeatureLevel, poleCount: number, orderType: OrderType | undefined): ISail2dBaseForm {
    return {
      id: IdGen.unsecure.get(),
      count: 1,
      version,
      orderType,
      clothCount: 1,
      productType: "sunSail2d",
      raffType: RaffType.None,
      cloth: undefined,
      colorId: "",
      shoppingList: ShoppingList.createEmpty(),
      articleSlots: {},
      securityCheckOptions: {},
      edges: {},
      featureLevel,
      poles: mapCount<ISCPole>(poleCount, () => ({})),
    };
  },
  createCurtainBase(curtainType: CurtainType, version: ProductVersion, articleMap: Record<string, IArticle>, colorMap: Record<string, IColorItem>, featureLevel: FeatureLevel): ICurtain2dForm {
    return {
      id: IdGen.unsecure.get(),
      count: 1,
      version: "2022",
      featureLevel,
      orderType: "S",
      shoppingList: ShoppingList.createEmpty(),
      productType: "curtain2d",
      curtainType,
      articleSlots: {},
      cloth: undefined,
      colorId: "",
      tracks: [],
      clothGlobal: CurtainData.createClothGlobal(curtainType, version, featureLevel, articleMap, colorMap),
      clothes: [],
      ropes: [],
      suspensionCount: "auto",
      extraTieBacks: []
    }
  },
  createAMCX3(sunSailType: ISail2dAMCXBaseForm["sunSailType"], version: ProductVersion, featureLevel: FeatureLevel): ISail2dAMCXBaseForm {
    const poleCount = 3;
    const pd: IProductDescriptor = { pdt: "sunSail", sunSailType, poleCount, version };
    return {
      sunSailType,
      poleCount,
      ...this.createBase(version, featureLevel, poleCount, Product.calcOrderType(pd, "2d").initial),
      inOutBPriority: "none",
      axleType: SunSailLogic.getAxleTypeInternal(sunSailType, version, undefined),
      yAB: SunSailLogic.getMinAllowedAxleDistIn(sunSailType, poleCount, version, PoleIndex.A, false) * 1000,
      yCB: SunSailLogic.getMinAllowedAxleDistIn(sunSailType, poleCount, version, PoleIndex.C, false) * 1000,
    };
  },
  createAMCX4(sunSailType: ISail2dAMCXBaseForm["sunSailType"], version: ProductVersion, featureLevel: FeatureLevel): ISail2dAMCXBaseForm {
    const poleCount = 4;
    const pd: IProductDescriptor = { pdt: "sunSail", sunSailType, poleCount, version };

    return {
      sunSailType,
      poleCount,
      ...this.createBase(version, featureLevel, poleCount, Product.calcOrderType(pd, "2d").initial),
      alignDistOut: true,
      inOutBPriority: "none",
      inOutDPriority: "none",
      outBDPriority: "B",
      axleType: SunSailLogic.getAxleTypeInternal(sunSailType, version, undefined),
      yAB: SunSailLogic.getMinAllowedAxleDistIn(sunSailType, poleCount, version, PoleIndex.A, false) * 1000,
      yAD: SunSailLogic.getMinAllowedAxleDistIn(sunSailType, poleCount, version, PoleIndex.A, false) * 1000,
      yCB: SunSailLogic.getMinAllowedAxleDistIn(sunSailType, poleCount, version, PoleIndex.C, false) * 1000,
      yCD: SunSailLogic.getMinAllowedAxleDistIn(sunSailType, poleCount, version, PoleIndex.C, false) * 1000,
    };
  },
  createCSXS4(sunSailType: ISail2dCSXSTwinBaseForm["sunSailType"], version: ProductVersion, featureLevel: FeatureLevel): ISail2dCSXSTwinBaseForm {
    const poleCount = 4;
    const pd: IProductDescriptor = { pdt: "sunSail", sunSailType, poleCount, version };

    return {
      sunSailType,
      poleCount,
      axleType: SunSailLogic.getAxleTypeInternal(sunSailType, version, undefined),
      inOutCPriority: "out",
      calculationType: "diagonals",
      ...this.createBase(version, featureLevel, poleCount, Product.calcOrderType(pd, "2d").initial),
    };
  },
  createCSXS3(sunSailType: ISail2dCSXSTwinBaseForm["sunSailType"], version: ProductVersion, featureLevel: FeatureLevel): ISail2dCSXSTwinBaseForm {
    const poleCount = 3;
    const pd: IProductDescriptor = { pdt: "sunSail", sunSailType, poleCount, version };

    return {
      sunSailType,
      poleCount,
      axleType: SunSailLogic.getAxleTypeInternal(sunSailType, version, undefined),
      inOutCPriority: "in",
      ...this.createBase(version, featureLevel, poleCount, Product.calcOrderType(pd, "2d").initial),
    };
  },
  createSolidayA3(version: ProductVersion, featureLevel: FeatureLevel): ISail2dAMCXBaseForm {
    return {
      ...this.createAMCX3(SunSailType.SolidayA, version, featureLevel),
      sunSailType: SunSailType.SolidayA,
      coilUpType: "clamp",
      coilUpPole: "B",
    };
  },
  createSolidayA4(version: ProductVersion, featureLevel: FeatureLevel): ISail2dAMCXBaseForm {
    return {
      ...this.createAMCX4(SunSailType.SolidayA, version, featureLevel),
      sunSailType: SunSailType.SolidayA,
      coilUpType: "clamp",
      coilUpPole: "B",
      twistingSet: TwistingSetType.Pole,
    };
  },
  createSolidayOne3(version: ProductVersion, featureLevel: FeatureLevel): ISail2dAMCXBaseForm {
    return {
      ...this.createAMCX3(SunSailType.SolidayOne, version, featureLevel),
      sunSailType: SunSailType.SolidayOne,
    };
  },
  createSolidayOne4(version: ProductVersion, featureLevel: FeatureLevel): ISail2dAMCXBaseForm {
    return {
      ...this.createAMCX4(SunSailType.SolidayOne, version, featureLevel),
      sunSailType: SunSailType.SolidayOne,
      twistingSet: TwistingSetType.Pole,
      coilUpDeflectionPosition: SunSailLogic.getDefaultCoilUpDeflectionPosition(),
      coilUpDeflectionOpposite: "heightChange"
    };
  },
  createSolidayFlex3(version: ProductVersion, featureLevel: FeatureLevel): ISail2dAMCXBaseForm {
    return {
      ...this.createAMCX3(SunSailType.SolidayFlex, version, featureLevel),
      sunSailType: SunSailType.SolidayFlex,
    };
  },
  createSolidayFlex4(version: ProductVersion, featureLevel: FeatureLevel): ISail2dAMCXBaseForm {
    return {
      ...this.createAMCX4(SunSailType.SolidayFlex, version, featureLevel),
      sunSailType: SunSailType.SolidayFlex,
      twistingSet: TwistingSetType.Pole,
    };
  },
  createSolidayC4(version: ProductVersion, featureLevel: FeatureLevel): ISail2dAMCXBaseForm {
    return {
      ...this.createAMCX4(SunSailType.SolidayC, version, featureLevel),
      sunSailType: SunSailType.SolidayC,
      pylons: [undefined, "ZT1000", undefined, "ZT1000"],
    };
  },
  createSolidayM4(version: ProductVersion, featureLevel: FeatureLevel): ISail2dAMCXBaseForm {
    return {
      ...this.createAMCX4(SunSailType.SolidayM, version, featureLevel),
      sunSailType: SunSailType.SolidayM,
      coilUpType: "clamp",
      coilUpPole: "B",
      twistingSet: TwistingSetType.Pole,
    };
  },
  createSolidayM3(version: ProductVersion, featureLevel: FeatureLevel): ISail2dAMCXBaseForm {
    return {
      ...this.createAMCX3(SunSailType.SolidayM, version, featureLevel),
      coilUpType: "clamp",
      twistingSet: TwistingSetType.Pole,
    };
  },
  createSolidayX4(version: ProductVersion, featureLevel: FeatureLevel): ISail2dAMCXBaseForm {
    return {
      ...this.createAMCX4(SunSailType.SolidayX, version, featureLevel),
      sunSailType: SunSailType.SolidayX,
      coilUpPole: "B",
      twistingSet: TwistingSetType.Pole,
    };
  },
  createSolidayCS4(version: ProductVersion, featureLevel: FeatureLevel): ISail2dCSXSTwinBaseForm {
    return {
      ...this.createCSXS4(SunSailType.SolidayCS, version, featureLevel),
      pylons: [undefined, undefined, "ZT1000", "ZT1000"],
      cs4PositionMode: ["rigid"],
    };
  },
  createSolidayCS3(version: ProductVersion, featureLevel: FeatureLevel): ISail2dCSXSTwinBaseForm {
    return {
      ...this.createCSXS3(SunSailType.SolidayCS, version, featureLevel),
      pylons: [undefined, undefined, "ZT1000"],
    };
  },
  createSolidayTwin5(version: ProductVersion, featureLevel: FeatureLevel): ISail2dCSXSTwinBaseForm {
    const sunSailType = SunSailType.SolidayTwin;
    const poleCount = 5;
    const pd: IProductDescriptor = { pdt: "sunSail", sunSailType, poleCount, version };

    return {
      sunSailType,
      poleCount,
      ...this.createBase(version, featureLevel, poleCount, Product.calcOrderType(pd, "2d").initial),
      axleType: SunSailLogic.getAxleTypeInternal(sunSailType, version, undefined),
      inOutCPriority: "out",
      inOutEPriority: "out",
      cs4PositionMode: ["rigid"],
      calculationType: "diagonals",
    };
  },
  createSolidayTwin6(version: ProductVersion, featureLevel: FeatureLevel): ISail2dCSXSTwinBaseForm {
    const sunSailType = SunSailType.SolidayTwin;
    const poleCount = 6;
    const pd: IProductDescriptor = { pdt: "sunSail", sunSailType, poleCount, version };

    return {
      sunSailType,
      poleCount,
      ...this.createBase(version, featureLevel, poleCount, Product.calcOrderType(pd, "2d").initial),
      axleType: SunSailLogic.getAxleTypeInternal(sunSailType, version, undefined),
      inOutCPriority: "out",
      inOutEPriority: "out",
      cs4PositionMode: ["rigid", "rigid"],
      calculationType: "diagonals",
    };
  },
  createSolidayXS4(version: ProductVersion, featureLevel: FeatureLevel): ISail2dCSXSTwinBaseForm {
    return {
      ...this.createCSXS4(SunSailType.SolidayXS, version, featureLevel),
    };
  },
  createSolidayXS3(version: ProductVersion, featureLevel: FeatureLevel): ISail2dCSXSTwinBaseForm {
    return {
      ...this.createCSXS3(SunSailType.SolidayXS, version, featureLevel),
      coilUpPole: "C",
    };
  },
  createSolidayFixed4(version: ProductVersion, featureLevel: FeatureLevel): ISail2dFixedForm {
    const poleCount = 4;
    const sunSailType = SunSailType.Fixed;
    const pd: IProductDescriptor = { pdt: "sunSail", sunSailType, poleCount, version };

    return {
      sunSailType,
      poleCount,
      ...this.createBase(version, featureLevel, poleCount, Product.calcOrderType(pd, "2d").initial),
      cornerTypes: [CornerType.D_Ring25, CornerType.D_Ring25, CornerType.D_Ring25, CornerType.D_Ring25],
      waterDirections: ["-"],
      sailBordersType: "individual",
      edges: {}
    };
  },
  createSolidayFixed3(version: ProductVersion, featureLevel: FeatureLevel): ISail2dFixedForm {
    const poleCount = 3;
    const sunSailType = SunSailType.Fixed;
    const pd: IProductDescriptor = { pdt: "sunSail", sunSailType, poleCount, version };

    return {
      sunSailType,
      poleCount,
      ...this.createBase(version, featureLevel, poleCount, Product.calcOrderType(pd, "2d").initial),
      cornerTypes: [CornerType.D_Ring25, CornerType.D_Ring25, CornerType.D_Ring25],
      waterDirections: ["-"],
      sailBordersType: "individual",
      edges: {}
    };
  },
  createSandyRect(pd: ISunSailDescriptor, featureLevel: FeatureLevel): ISail2dSandy {
    const sunSailType = SunSailType.SandyRect;
    const ssd: ISail2dSandy = {
      sunSailType,
      poleCount: pd.poleCount,
      dimensions: SandyLogic.calcInitialSandyDimensions(sunSailType, pd.poleCount),
      ...this.createBase(pd.version, featureLevel, pd.poleCount, Product.calcOrderType(pd, "2d").initial),
      centerPoles: []
    };
    const poleCount = pd.poleCount;
    ssd.poles = mapCount(poleCount, (i) => createInitialSCPole(ssd, poleCount, i, featureLevel));
    ssd.poles = mapCount(poleCount, (i) => fillInitialPole(ssd, i));
    return ssd;
  },
  createSandyPoly(pd: ISunSailDescriptor, featureLevel: FeatureLevel): ISail2dSandy {
    const sunSailType = SunSailType.SandyPoly;
    const ssd: ISail2dSandy = {
      sunSailType,
      poleCount: pd.poleCount,
      dimensions: SandyLogic.calcInitialSandyDimensions(sunSailType, pd.poleCount),
      ...this.createBase(pd.version, featureLevel, pd.poleCount, Product.calcOrderType(pd, "2d").initial),
      poles: mapCount<ISCPole>(pd.poleCount, () => ({
        poleType: PoleType.Pole,
      })),
      centerPoles: []
    };
    const poleCount = pd.poleCount;
    ssd.poles = mapCount(poleCount, (i) => createInitialSCPole(ssd, poleCount, i, featureLevel));
    ssd.poles = mapCount(poleCount, (i) => fillInitialPole(ssd, i));
    return ssd;
  },
  createSunSail(pd: IProductDescriptor, featureLevel: FeatureLevel): ISail2dForm | undefined {
    if (PD.isSail(pd)) {
      switch (pd.sunSailType) {
        case SunSailType.SolidayA:
          return pd.poleCount === 3 ? this.createSolidayA3(pd.version, featureLevel) : this.createSolidayA4(pd.version, featureLevel);
        case SunSailType.SolidayOne:
          return pd.poleCount === 3 ? this.createSolidayOne3(pd.version, featureLevel) : this.createSolidayOne4(pd.version, featureLevel);
        case SunSailType.SolidayFlex:
          return pd.poleCount === 3 ? this.createSolidayFlex3(pd.version, featureLevel) : this.createSolidayFlex4(pd.version, featureLevel);
        case SunSailType.SolidayM:
          return pd.poleCount === 3 ? this.createSolidayM3(pd.version, featureLevel) : this.createSolidayM4(pd.version, featureLevel);
        case SunSailType.SolidayC:
          return this.createSolidayC4(pd.version, featureLevel);
        case SunSailType.SolidayCS:
          return pd.poleCount === 3 ? this.createSolidayCS3(pd.version, featureLevel) : this.createSolidayCS4(pd.version, featureLevel);
        case SunSailType.SolidayX:
          return this.createSolidayX4(pd.version, featureLevel);
        case SunSailType.SolidayXS:
          return pd.poleCount === 3 ? this.createSolidayXS3(pd.version, featureLevel) : this.createSolidayXS4(pd.version, featureLevel);
        case SunSailType.Fixed:
          return pd.poleCount === 3 ? this.createSolidayFixed3(pd.version, featureLevel) : this.createSolidayFixed4(pd.version, featureLevel);
        case SunSailType.SolidayTwin:
          return pd.poleCount === 5 ? this.createSolidayTwin5(pd.version, featureLevel) : this.createSolidayTwin6(pd.version, featureLevel);
        case SunSailType.SandyRect:
          return this.createSandyRect(pd, featureLevel)
        case SunSailType.SandyPoly:
          return this.createSandyPoly(pd, featureLevel)
      }
    }
    throw new Error("createSunSailType: Not supported so far!");
  },

  createStandardCollection(pd: IProductDescriptor, featureLevel: FeatureLevel): IStandardCollection2dForm | undefined {
    if (PD.isStandardCollection(pd)) {
      return StandardCollection2dForm.create(featureLevel);
    }
    throw new Error("createStandardCollection: Failed to create standard collection!");
  },
  createCurtain(pd: IProductDescriptor, articleMap: Record<string, IArticle>, colorMap: Record<string, IColorItem>, featureLevel: FeatureLevel): ICurtain2dForm | undefined {
    if (PD.isCurtain(pd)) {
      const curtain = this.createCurtainBase(pd.curtainType, pd.version, articleMap, colorMap, featureLevel);
      if (pd.curtainType === "guide") {
        curtain.tracks.push(Curtain2d.createTrack())
      } else if (pd.curtainType === "rope") {
        curtain.ropes.push(Curtain2d.createRope())
      }
      curtain.clothes.push(Curtain2d.createCloth("rect"));
      return curtain;
    }
    throw new Error("createCurtain: Not supported so far!");
  },
  createProduct(pd: IProductDescriptor, articleMap: Record<string, IArticle>, colorMap: Record<string, IColorItem>, featureLevel: FeatureLevel): IProduct2dForm | undefined {
    if (PD.isSail(pd)) {
      return this.createSunSail(pd, featureLevel);
    } else if (PD.isCurtain(pd)) {
      return this.createCurtain(pd, articleMap, colorMap, featureLevel);
    } else if (PD.isStandardCollection(pd)) {
      return this.createStandardCollection(pd, featureLevel);
    }
    throw new Error("createSunSailType: Not supported so far!");
  }
};

export const Sail2dProjectForm = {
  create(featureLevel: FeatureLevel, options?: { sunSail?: SunSailType, poleCount?: number, version?: ProductVersion }): ISail2dProjectForm {
    const pd: IProductDescriptor = PD.interned({
      pdt: "sunSail",
      sunSailType: SunSailType.SolidayA,
      poleCount: 4,
      version: "2022",
      ...options
    })
    return {
      version: 101,
      dealer: ProjectDealer.create(),
      endCustomer: Customer.create(),
      orderInfo: ProjectOrderInfo.create({ customerSurcharge: 0, discountOverrides: undefined }),
      sunSail: Sail2dForm.createSunSail(pd, featureLevel)!,
    };
  },
  fillCompany(inputModel: ISail2dProjectForm, c: IBeosCompany | undefined) {
    if (!c) return;
    const split = CompanyHelper.splitAddress(c.address);
    inputModel.dealer.companyId = c._id;
    inputModel.dealer.street = split.street;
    inputModel.dealer.number = split.number;
    inputModel.dealer.zip = c.zip;
    inputModel.dealer.city = c.city;
    inputModel.dealer.country = c.country;
  },
};